import { client } from '@/lib/api/client'
import {
  venueReservationRefundsExportPath,
  venueReservationRefundsPath,
  venueReservationRefundSummaryPath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'
import { downloadBlob } from '@/lib/utils/downloadFile'

import type { ReservationRefundListView, ReservationRefundSummaryView } from './reservationRefundTypes'
import { refundListQueryParams, refundsCsvFileName, type ReservationRefundListFilter } from './reservationRefundViewModel'

/**
 * `GET /venue/v1/manager/venues/{venueId}/reservation-refunds/summary?yearMonth=` (#589).
 *
 * 🚨 게이팅 대상이 아니다 — 항상 200. `yearMonth` 는 KST 로컬 값(`YYYY-MM`)이며 호출부가
 * `currentYearMonthKst()`(`dashboardViewModel.ts`)로 계산해 넘긴다.
 */
export async function fetchReservationRefundSummary(
  venueId: string,
  yearMonth: string
): Promise<ReservationRefundSummaryView> {
  try {
    const { data } = await client.get<ReservationRefundSummaryView>(
      venueReservationRefundSummaryPath(venueId),
      { params: { yearMonth } }
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../reservation-refunds` (#589 §4-2 + #703 §5) — 건별 목록, 커서 페이지네이션.
 * 🚨 위 summary(월별 집계)와는 다른 EP다 — `totalCount`는 deprecated(현재 페이지 건수와 같다),
 * `pageCount`/`nextExist`/`nextCursor`로 이어 읽는다.
 */
export async function fetchReservationRefunds(
  venueId: string,
  filter: ReservationRefundListFilter,
  cursor?: string
): Promise<ReservationRefundListView> {
  try {
    const { data } = await client.get<ReservationRefundListView>(venueReservationRefundsPath(venueId), {
      params: refundListQueryParams(filter, cursor),
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../reservation-refunds/export` (#703 §6) — 목록과 동일 필터를 그대로 전달하는 CSV 다운로드.
 * 🚨 파일명은 `Content-Disposition` 파싱에 기대지 않고 api-spec 문서화 패턴을 클라이언트가 구성한다.
 */
export async function downloadReservationRefundsCsv(
  venueId: string,
  filter: ReservationRefundListFilter
): Promise<void> {
  try {
    const { data } = await client.get<Blob>(venueReservationRefundsExportPath(venueId), {
      params: refundListQueryParams(filter),
      responseType: 'blob',
    })
    downloadBlob(data, refundsCsvFileName(venueId, filter.from, filter.to))
  } catch (error) {
    throw toApiError(error)
  }
}
